/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-10 09:55:40
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { openDB, deleteDB } from "idb";

import { Properties } from "@/frameworks/libraries/properties";
import { Configure, Environments, Logger } from "@/frameworks/libraries/utilities";

import * as User from "./users";
import * as Favorites from "./favorites";
import * as Groups from "./groups";
import * as Chats from "./chats";
import * as States from "./states";
import * as Unity from "./unity";
import * as Spaces from "./spaces";
import * as MydeskWidget from "./mydeskWidgets";

export type Persistence = User.UserStates &
	Favorites.FavoriteStates &
	Groups.GroupStates &
	Chats.ChatStates &
	Spaces.SpaceStates &
	States.StateStates &
	Unity.UnityStates &
	MydeskWidget.MydeskWidgetStates & {
		has_hydrated: boolean;
		set_hydrated: (value: boolean) => void;
	};

const version = Environments.env("ROOT_VERSION") as string;
const DB_NAME = "rsupportPersistence";
const DB_OBJECT_STORE_NAME = "persistenceStore";

const database = openDB(DB_NAME, parseInt(version.replace(/\./g, "")), {
	upgrade(db, oldVersion, newVersion) {
		console.log("upgrade", oldVersion, newVersion);
		// 기존 스토어가 있으면 삭제하지 않고 유지
		if (!db.objectStoreNames.contains(DB_OBJECT_STORE_NAME)) {
			db.createObjectStore(DB_OBJECT_STORE_NAME);
		}
	},
	blocked(currentVersion, blockedVersion, event) {
		console.log("blocked", currentVersion, blockedVersion, event);
	}
});

// 데이터베이스 연결 시도 시 에러 처리 추가
const getDatabase = async () => {
	try {
		return await database;
	} catch (error) {
		console.error("Database connection error:", error);
		// 에러 발생 시 기존 데이터베이스 삭제 후 재시도
		if (error instanceof Error && error.name === "VersionError") {
			await deleteDB(DB_NAME);
			return await openDB(DB_NAME, parseInt(version.replace(/\./g, "")), {
				upgrade(db) {
					db.createObjectStore(DB_OBJECT_STORE_NAME);
				}
			});
		}
		throw error;
	}
};

/**
 * usePersistStore is a state management utility that leverages persistent storage and devtools integration.
 *
 * It initializes the state with combined initial states from various modules including Users, Favorites, Groups, Chats, and States.
 * The state is enhanced with persistence capabilities, ensuring that specific state slices are saved and restored
 * between sessions based on the provided persist_options configuration.
 *
 * This allows for advanced state management features such as debugging with devtools and selective state persistence.
 */

const usePersistStore = create(
	persist(
		devtools(
			subscribeWithSelector<Persistence>((set, get) => ({
				...User.InitialUserStates,
				...Favorites.InitialFavoriteStates,
				...Groups.InitialGroupStates,
				...Chats.InitialChatStates,
				...States.InitialStateStates,
				...Unity.InitialUnityStates,
				...Spaces.InitialSpaceStates,
				...MydeskWidget.InitialMydeskWidgetStates,
				has_hydrated: false,
				set_hydrated: (value: boolean) => set({ has_hydrated: value })
			})),
			{ enabled: Environments.env<string>("PRODUCT_MODE") === "dev" }
		),
		{
			name: `rsupportPersistence-${Configure.userID}`,
			storage: {
				getItem: async (name: string) => {
					await Properties.init_finger_print();
					name = Configure.userID + "-" + Properties.finger_print;
					try {
						const db = await getDatabase();
						return db.get("persistenceStore", name) || null;
					} catch (error) {
						console.error("getItem error:", error);
						return null;
					}
				},
				setItem: async (name: string, value): Promise<void> => {
					await Properties.init_finger_print();
					name = Configure.userID + "-" + Properties.finger_print;
					if (Configure.userID.length > 0 && name.length > 0) {
						try {
							const db = await getDatabase();
							await db.put("persistenceStore", value, name);
						} catch (error) {
							console.error("setItem error:", error);
						}
					}
				},
				removeItem: async (name: string): Promise<void> => {
					await Properties.init_finger_print();
					name = Configure.userID + "-" + Properties.finger_print;
					if (Configure.userID.length > 0 && name.length > 0) {
						try {
							const db = await getDatabase();
							await db.delete("persistenceStore", name);
						} catch (error) {
							console.error("removeItem error:", error);
						}
					}
				}
			},
			// ref by https://zustand.docs.pmnd.rs/middlewares/persist#persisting-a-state-partially
			partialize: (state: Omit<Persistence, "blocked_users" /* 저장하지 않을 항목은 이곳에 나열해주세요 */>) => ({
				// IndexedDB에 저장한 항목만 기록해주세요.
				groups: state.groups,
				users: state.users,
				favorites: state.favorites,
				rooms: state.rooms,
				draft: state.draft,
				unity: state.unity,
				space: state.space,
				states: state.states,
				mydeskWidgets: state.mydeskWidgets
			}),
			onRehydrateStorage(state) {
				if (state) {
					console.log("onRehydrateStorage", state);
				}

				return (_state, error) => {
					if (error) {
						console.error("onRehydrateStorage error", error);
						usePersistStore.persist.clearStorage();
					} else {
						state.set_hydrated(true);
						console.debug("rehydrated");
					}
				};
			}
		}
	)
);

States.StateSubscribes.forEach((subscribe) => {
	usePersistStore.subscribe(subscribe.subscribe, subscribe.callback, subscribe.options);
});

// Spaces.SpaceSubscribes.forEach((subscribe) => {
// 	usePersistStore.subscribe(subscribe.subscribe, subscribe.callback, subscribe.options);
// });

export default usePersistStore;
